/**
 * Journey progress (PROJ-155). Residents had no sense of how far through the
 * conversation they were.
 *
 * The backend anchors the bar to the STAGE the resident is in (1–5), so a
 * skipped question never makes it lie, and sends a `fraction` that also
 * advances *within* a stage — the stages are uneven (stage 1 holds nine nodes,
 * the rest three each), so a stage-only bar sat frozen through the longest
 * stretch of the journey and then lurched.
 *
 * A line only: no step count, no stage name (Naveen, 11 Sept). The accessible
 * label still announces "Step n of 5: <stage>", which is now the only place
 * that wording appears.
 */
import { useRef } from "react";

import { useI18n } from "../i18n";
import type { Turn } from "../api/types";
import "./ProgressBar.css";

export function ProgressBar({ turn }: { turn: Turn | null | undefined }) {
  const { t } = useI18n();
  const p = turn?.progress;
  // A progress bar must never retreat. Config order is conversation order for
  // the common path, but a branch can revisit an earlier-declared node (e.g.
  // the fit-note question after the out-of-work questions), which would send
  // the fill backwards and read as lost progress. Hold the high-water mark.
  const peak = useRef(0);
  if (!p) {
    peak.current = 0;   // new conversation: start the mark over
    return null;
  }
  // The backend sends `fraction` (0–1), which advances on every question
  // rather than only when the stage changes — the stages are uneven, so a
  // stage-only bar sat frozen through the longest stretch. Falls back to the
  // old half-step fill for a backend that predates the field.
  // A floor of 4% so the bar is visibly *started* on the very first question.
  const raw = p.complete ? 1 : p.fraction ?? Math.max(p.step - 0.5, 0) / p.of;
  const floored = Math.max(raw, p.complete ? 1 : 0.04);
  peak.current = p.complete ? 1 : Math.max(peak.current, floored);
  const pct = Math.round(peak.current * 100);
  const stageLabel = p.complete ? t("progress.done") : t(`progress.stage${p.step}`);
  const stepText = p.complete ? t("progress.done") : t("progress.stepOf").replace("{step}", String(p.step)).replace("{of}", String(p.of));
  const label = p.complete ? stageLabel : `${stepText}: ${stageLabel}`;
  // The bar is the whole indicator — no visible step/stage text (Naveen,
  // 11 Sept). The label still carries "Step n of 5: <stage>" for screen
  // readers, which is the only place that wording now appears.
  return (
    <div className="progress" role="progressbar" aria-valuemin={0} aria-valuemax={p.of} aria-valuenow={p.complete ? p.of : p.step} aria-valuetext={label} aria-label={label}>
      <div className="progress__track"><div className="progress__fill" style={{ width: `${pct}%` }} /></div>
    </div>
  );
}
